import { useState } from 'react';
import { api, formatNumber, formatRelative } from '../api';
import { usePolling } from '../auth';
import { Badge, Card, Empty, Notice, Spinner } from '../components/ui';
import type { CandleStoreResponse, ProvidersResponse, Quote } from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

/**
 * Operational view of the market data layer. Because the app deliberately runs on
 * free and keyless feeds, knowing which one served a price, which are throttled,
 * and where the local candle store has gaps is part of trusting the results.
 */
export default function Providers({ pushToast }: { pushToast: Toast }) {
  const providers = usePolling<ProvidersResponse>(() => api.providers(), 30_000);
  const store = usePolling<CandleStoreResponse>(() => api.candleStore(), 60_000);
  const quotes = usePolling<{ quotes: Quote[]; failures: Array<{ symbol: string; error: string }>; usdToGbp: number | null }>(
    () => api.quotes(),
    60_000,
  );
  const [busy, setBusy] = useState<string | null>(null);

  async function run(label: string, action: () => Promise<unknown>): Promise<void> {
    setBusy(label);
    try {
      await action();
      pushToast(`${label} complete.`);
      providers.reload();
      store.reload();
      quotes.reload();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : `${label} failed`, 'error');
    } finally {
      setBusy(null);
    }
  }

  const storeSymbols = store.data?.symbols ?? [];
  const missingLse = (quotes.data?.quotes ?? [])
    .filter((quote) => quote.symbol.endsWith('.L'))
    .filter((quote) => !storeSymbols.some((entry) => entry.symbol === quote.symbol));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Data feeds</h1>
          <p>
            Six providers are wired up. Three need no API key at all, and the engine walks the chain
            until one answers, so a throttled feed degrades quality rather than stopping trading.
          </p>
        </div>
        <div className="btn-row">
          <button
            className="btn"
            type="button"
            disabled={busy !== null}
            onClick={() => void run('Backfill', () => api.backfill())}
          >
            {busy === 'Backfill' ? <Spinner label="Backfilling" /> : 'Backfill history'}
          </button>
          <button
            className="btn"
            type="button"
            disabled={busy !== null}
            onClick={() => void run('Circuit reset', () => api.resetCircuits())}
          >
            Reset circuit breakers
          </button>
          <button
            className="btn"
            type="button"
            disabled={busy !== null}
            onClick={() => void run('Cache clear', () => api.clearCache())}
          >
            Clear quote cache
          </button>
        </div>
      </div>

      {providers.error && <Notice tone="error">{providers.error}</Notice>}

      <Card title="Provider capability and health" subtitle={`last ${providers.data?.windowHours ?? 24} hours`}>
        {providers.data ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th className="num">Priority</th>
                  <th>Key</th>
                  <th>Quotes</th>
                  <th>History</th>
                  <th className="num">Calls</th>
                  <th className="num">Failures</th>
                  <th className="num">Success</th>
                  <th className="num">Avg latency</th>
                  <th>Circuit</th>
                  <th>Last error</th>
                </tr>
              </thead>
              <tbody>
                {providers.data.health.map((row) => {
                  const capability = providers.data?.providers.find(
                    (entry) => entry.name === row.provider,
                  );
                  return (
                    <tr key={row.provider}>
                      <td>
                        <strong>{row.provider}</strong>
                      </td>
                      <td className="num">{capability ? capability.priority : '—'}</td>
                      <td>
                        {!row.requiresKey ? (
                          <Badge tone="green">not needed</Badge>
                        ) : row.configured ? (
                          <Badge tone="green">set</Badge>
                        ) : (
                          <Badge tone="grey">missing</Badge>
                        )}
                      </td>
                      <td>{capability?.quotes ? 'yes' : 'no'}</td>
                      <td>{capability?.dailyCandles ? 'yes' : 'no'}</td>
                      <td className="num">{row.calls}</td>
                      <td className="num">
                        {row.failures > 0 ? <span className="neg">{row.failures}</span> : '0'}
                      </td>
                      <td className="num">
                        {row.calls === 0 ? (
                          '—'
                        ) : (
                          <span className={row.successRate > 90 ? 'pos' : row.successRate > 50 ? '' : 'neg'}>
                            {row.successRate.toFixed(0)}%
                          </span>
                        )}
                      </td>
                      <td className="num">{row.calls === 0 ? '—' : `${row.avgMs}ms`}</td>
                      <td>
                        {row.circuitOpen ? (
                          <Badge tone="red">open</Badge>
                        ) : (
                          <Badge tone="green">closed</Badge>
                        )}
                      </td>
                      <td className="reason" style={{ maxWidth: '32ch' }}>
                        {row.lastError ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Spinner label="Loading provider health" />
        )}
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
          A circuit opens after three consecutive failures and closes again automatically. While open,
          calls to that provider fail instantly so the chain moves on without waiting for a timeout.
        </p>
      </Card>

      {store.data && <HistoryCoverage store={store.data} onRepair={run} busy={busy} />}

      {missingLse.length > 0 && (
        <Notice tone="warn" title="Some London instruments have quotes but no stored history">
          {missingLse
            .slice(0, 10)
            .map((quote) => quote.symbol)
            .join(', ')}
          . They can be priced but not analysed, so no stream will trade them. London history comes
          from CNBC, with Yahoo as the fallback; the repair worker retries every few minutes, or run
          the deep backfill to load it all at once.
        </Notice>
      )}

      <Card
        title="Local candle store"
        subtitle={`${formatNumber(store.data?.totalBars ?? 0, 0)} daily bars stored`}
      >
        {storeSymbols.length === 0 ? (
          <Empty>No history stored yet. Use “Backfill history” above.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th className="num">Bars</th>
                  <th>Latest bar</th>
                  <th>Expected</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {storeSymbols.map((entry) => (
                  <tr key={entry.symbol}>
                    <td className="sym">{entry.symbol}</td>
                    <td className="num">{entry.bars}</td>
                    <td className="mono-small">{entry.latest}</td>
                    <td className="mono-small">{entry.expectedLatest}</td>
                    <td>
                      {entry.current ? (
                        <Badge tone="green">current</Badge>
                      ) : (
                        <Badge tone="amber">behind</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
          Completed daily bars never change, so they are fetched once and reused. That is what keeps
          the app inside the free providers' rate limits: roughly one request per instrument per day
          rather than one per decision cycle.
        </p>
      </Card>

      <Card
        title="Live quotes"
        subtitle={
          quotes.data?.usdToGbp
            ? `USD/GBP ${formatNumber(quotes.data.usdToGbp, 4)}`
            : 'FX rate not loaded'
        }
      >
        {quotes.data ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th className="num">Price</th>
                  <th className="num">Change</th>
                  <th>Feed</th>
                  <th>As of</th>
                </tr>
              </thead>
              <tbody>
                {quotes.data.quotes.map((quote) => (
                  <tr key={quote.symbol}>
                    <td className="sym">{quote.symbol}</td>
                    <td style={{ color: 'var(--text-dim)' }}>{quote.name ?? '—'}</td>
                    <td className="num">
                      {formatNumber(quote.price, 4)} {quote.currency}
                    </td>
                    <td className="num">
                      {quote.changePct === null ? (
                        '—'
                      ) : (
                        <span className={quote.changePct >= 0 ? 'pos' : 'neg'}>
                          {quote.changePct >= 0 ? '+' : ''}
                          {quote.changePct.toFixed(2)}%
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge tone={quote.stale ? 'amber' : 'grey'}>
                        {quote.provider}
                        {quote.stale ? ' (stale)' : ''}
                      </Badge>
                    </td>
                    <td className="mono-small">{formatRelative(quote.asOf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Spinner label="Loading quotes" />
        )}
        {quotes.data && quotes.data.failures.length > 0 && (
          <details className="expand" style={{ marginTop: 12 }}>
            <summary>{quotes.data.failures.length} instrument(s) could not be priced</summary>
            <pre>
              {quotes.data.failures.map((failure) => `${failure.symbol}: ${failure.error}`).join('\n')}
            </pre>
          </details>
        )}
      </Card>

      {providers.data && providers.data.divergences.length > 0 && (
        <Card
          title="Price disagreements"
          subtitle="recorded when two feeds differ by more than the configured threshold"
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Symbol</th>
                  <th>Feed A</th>
                  <th className="num">Price A</th>
                  <th>Feed B</th>
                  <th className="num">Price B</th>
                  <th className="num">Difference</th>
                </tr>
              </thead>
              <tbody>
                {providers.data.divergences.map((entry, index) => (
                  <tr key={`${entry.symbol}-${index}`}>
                    <td className="mono-small">{formatRelative(entry.createdAt)}</td>
                    <td className="sym">{entry.symbol}</td>
                    <td>{entry.providerA}</td>
                    <td className="num">{formatNumber(entry.priceA, 4)}</td>
                    <td>{entry.providerB}</td>
                    <td className="num">{formatNumber(entry.priceB, 4)}</td>
                    <td className="num neg">{entry.divergencePct.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * History coverage and the background repair worker.
 *
 * Instruments without stored daily bars are invisible to every strategy, so a
 * coverage gap silently shrinks the universe. Showing it explicitly, along with
 * what the worker is doing about it, means a half-dead universe cannot go
 * unnoticed.
 */
function HistoryCoverage({
  store,
  onRepair,
  busy,
}: {
  store: CandleStoreResponse;
  onRepair: (label: string, action: () => Promise<unknown>) => Promise<void>;
  busy: string | null;
}) {
  const { gaps, worker } = store;
  const lseGaps = gaps.filter((gap) => gap.symbol.endsWith('.L'));
  const usGaps = gaps.filter((gap) => !gap.symbol.endsWith('.L'));

  const reasonLabel: Record<string, string> = {
    'no-history': 'no bars at all',
    'insufficient-bars': 'too few bars for indicators',
    stale: 'behind the last session',
  };

  return (
    <Card
      title="History coverage"
      subtitle={
        gaps.length === 0
          ? 'every instrument has usable history'
          : `${gaps.length} instrument(s) cannot be analysed`
      }
      actions={
        <>
          <Badge tone={worker.active ? 'green' : 'grey'}>
            repair worker {worker.active ? 'running' : 'off'}
          </Badge>
          <button
            className="btn btn-sm"
            type="button"
            disabled={busy !== null || gaps.length === 0}
            onClick={() => void onRepair('History repair', () => api.repairHistory())}
          >
            {busy === 'History repair' ? <Spinner /> : 'Repair now'}
          </button>
        </>
      }
    >
      {gaps.length === 0 ? (
        <Notice tone="info">
          All {store.symbols.length} instruments have enough stored history for the indicators. Every
          stream can see the full universe.
        </Notice>
      ) : (
        <>
          <div className="grid grid-3" style={{ marginBottom: 12 }}>
            <div>
              <div className="stat-label">Tradable</div>
              <div className="stat-value" style={{ fontSize: 19 }}>
                {store.symbols.length - gaps.length}
                <span style={{ fontSize: 13, color: 'var(--text-faint)' }}>
                  {' '}
                  / {store.symbols.length + gaps.filter((gap) => gap.bars === 0).length}
                </span>
              </div>
              <div className="stat-foot">instruments with usable bars</div>
            </div>
            <div>
              <div className="stat-label">Gaps by venue</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                <div>
                  London: <strong className={lseGaps.length > 0 ? 'neg' : 'pos'}>{lseGaps.length}</strong>
                </div>
                <div>
                  US: <strong className={usGaps.length > 0 ? 'neg' : 'pos'}>{usGaps.length}</strong>
                </div>
              </div>
            </div>
            <div>
              <div className="stat-label">Repair worker</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                <div>{worker.passes} pass(es) run</div>
                <div style={{ color: 'var(--text-faint)' }}>
                  last {formatRelative(worker.lastRunAt)}
                </div>
                {worker.repairedThisSession.length > 0 && (
                  <div className="pos">fixed {worker.repairedThisSession.length} this session</div>
                )}
              </div>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th className="num">Bars stored</th>
                  <th>Latest</th>
                  <th>Expected</th>
                  <th>Problem</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map((gap) => (
                  <tr key={gap.symbol}>
                    <td className="sym">{gap.symbol}</td>
                    <td className="num">{gap.bars}</td>
                    <td className="mono-small">{gap.latest ?? '—'}</td>
                    <td className="mono-small">{gap.expected}</td>
                    <td>
                      <Badge tone={gap.reason === 'stale' ? 'amber' : 'red'}>
                        {reasonLabel[gap.reason] ?? gap.reason}
                      </Badge>
                      {worker.coldSymbols.includes(gap.symbol) && (
                        <span className="mono-small"> repeatedly failing, retried rarely</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
            The worker retries a few of these every{' '}
            {worker.active ? 'few minutes' : 'pass, when enabled'} and skips providers whose circuit
            is open, so coverage repairs itself once a rate limit expires. Instruments listed here are
            excluded from every stream's decisions until they have at least 30 stored bars.
          </p>
        </>
      )}
    </Card>
  );
}
